import { useRef, useCallback } from 'react';
import type { State as GridState, State } from '../../controllers/grid/types.tsx';
import { selectCellHeight, selectCellWidth } from '../../controllers/grid/selectors.tsx';
import type { RowConfig } from '../../common/types.tsx';
import type { Rect, ScrollIntoViewParams as Params } from './types.tsx';

export const getMaxHorizontalScroll = ({
	widths,
	viewport,
}: {
	widths: number[];
	viewport: Params['viewport'];
}) => Math.max(widths.reduce((acc, width) => acc + width, 0) - viewport.width, 0);

export const getMaxVerticalScroll = ({
	rows,
	viewport,
}: {
	rows: RowConfig[];
	viewport: Params['viewport'];
}) => {
	const lastRow = rows[rows.length - 1];
	return Math.max(lastRow.top + lastRow.height - viewport.height, 0);
};

export const getGridElementRect = ({
	state,
	viewport,
	scrollX,
	scrollY,
	row,
	column,
}: {
	state: State;
	viewport: Params['viewport'];
	scrollX: number;
	scrollY: number;
	row: number;
	column: number;
}) => {
	const { widths, rows } = state;

	const top = viewport.top - scrollY + rows[row].top;

	const left =
		viewport.left - scrollX + widths.slice(0, column).reduce((acc, width) => acc + width, 0);

	const width = selectCellWidth(state, { row, column, preview: false });
	const height = selectCellHeight(state, { row, column });

	return getRect({ top, left, width, height });
};

export const getNextVerticalScroll = ({
	isInHeaderRow,
	scrollY,
	targetRect,
	viewportRect,
}: {
	isInHeaderRow: boolean;
	scrollY: number;
	targetRect: Rect;
	viewportRect: Rect;
}) => {
	const canScrollVertically = !isInHeaderRow;

	const shouldScrollBackwardVertically = canScrollVertically && targetRect.y1 < viewportRect.y1;

	const shouldScrollForwardVertically = canScrollVertically && targetRect.y2 > viewportRect.y2;

	if (shouldScrollBackwardVertically === shouldScrollForwardVertically) {
		return undefined;
	}

	const shouldScrollToMiddle = targetRect.height < viewportRect.height;
	const scrollToMiddleOffset = viewportRect.height / 2 - targetRect.height / 2;

	if (shouldScrollBackwardVertically) {
		return (
			scrollY + targetRect.y1 - viewportRect.y1 - (shouldScrollToMiddle ? scrollToMiddleOffset : 0)
		);
	}

	if (shouldScrollForwardVertically) {
		return (
			scrollY + targetRect.y2 - viewportRect.y2 + (shouldScrollToMiddle ? scrollToMiddleOffset : 0)
		);
	}

	return undefined;
};

export const getNextHorizontalScroll = ({
	isInStickyColumn,
	scrollX,
	targetRect,
	viewportRect,
}: {
	isInStickyColumn: boolean;
	scrollX: number;
	targetRect: Rect;
	viewportRect: Rect;
}) => {
	const isLargerThanViewport = targetRect.width > viewportRect.width;

	const shouldScrollBackwardHorizontally = !isInStickyColumn && targetRect.x1 < viewportRect.x1;
	const shouldScrollForwardHorizontally = !isInStickyColumn && targetRect.x2 > viewportRect.x2;

	if (shouldScrollBackwardHorizontally === shouldScrollForwardHorizontally) {
		return undefined;
	}

	const shouldScrollToMiddle = !isLargerThanViewport;
	const scrollToMiddleOffset = shouldScrollToMiddle
		? viewportRect.width / 2 - targetRect.width / 2
		: 0;

	const leftVisibleScroll = scrollX + targetRect.x1 - viewportRect.x1 - scrollToMiddleOffset;
	const rightVisibleScroll = scrollX + targetRect.x2 - viewportRect.x2 + scrollToMiddleOffset;

	if (shouldScrollBackwardHorizontally) {
		return leftVisibleScroll;
	}

	if (shouldScrollForwardHorizontally) {
		if (isLargerThanViewport) {
			return leftVisibleScroll;
		}

		return rightVisibleScroll;
	}
};

export const useScrollIntoView = (params: Params) => {
	const paramsRef = useRef(params);
	paramsRef.current = params;

	return useCallback(
		(row: number, column: number, /** The target element if it's mounted */ target?: HTMLElement) =>
			(state: GridState) => {
				const { viewport, scrollTo, scrollPadding, containerRef } = paramsRef.current;
				const { rows, columns, widths } = state;

				const scrollX = containerRef.current?.scrollLeft ?? 0;
				const scrollY = containerRef.current?.scrollTop ?? 0;

				const isInHeaderRow = rows[row]?.thead ?? false;
				const isInStickyColumn = columns[column]?.sticky ?? true;

				const paddingLeft =
					scrollPadding.left ??
					columns.reduce((acc, col, index) => acc + (col.sticky ? widths[index] : 0), 0);

				const paddingTop = scrollPadding.top ?? 0;
				const paddingRight = scrollPadding.right ?? 0;
				const paddingBottom = scrollPadding.bottom ?? 0;

				const viewportRect = getRect({
					top: viewport.top + paddingTop,
					left: viewport.left + paddingLeft,
					width: viewport.width - (paddingLeft + paddingRight),
					height: viewport.height - (paddingTop + paddingBottom),
				});

				const targetRect = target
					? getRect(target.getBoundingClientRect())
					: getGridElementRect({ state, viewport, scrollX, scrollY, row, column });

				const nextScrollY = getNextVerticalScroll({
					isInHeaderRow,
					scrollY,
					targetRect,
					viewportRect,
				});

				const nextScrollX = getNextHorizontalScroll({
					isInStickyColumn,
					scrollX,
					targetRect,
					viewportRect,
				});

				const maxVerticalScroll = getMaxVerticalScroll({ rows, viewport });
				const maxHorizontalScroll = getMaxHorizontalScroll({ widths, viewport });

				if (nextScrollX === undefined && nextScrollY === undefined) {
					return;
				}

				scrollTo({
					x:
						nextScrollX !== undefined
							? Math.min(Math.max(nextScrollX, 0), maxHorizontalScroll)
							: undefined,
					y:
						nextScrollY !== undefined
							? Math.min(Math.max(nextScrollY, 0), maxVerticalScroll)
							: undefined,
					smooth: false,
				});
			},
		[],
	);
};

export const getRect = ({
	top,
	height,
	left,
	width,
}: {
	top: number;
	height: number;
	left: number;
	width: number;
}): Rect => ({
	x1: left,
	y1: top,
	x2: left + width,
	y2: top + height,
	width,
	height,
});
